import {
  Box,
  Heading,
  InlineStack,
  NumberField,
  SegmentedControl,
  Stack,
  Strong,
  Text,
} from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import {
  ADD_FUNDS_PRESETS_DOLLARS,
  coveringAmountDollars,
  CUSTOM_AMOUNT_RANGE,
  fundingTag,
  type AddFundsFunding,
  type AmountSelection,
} from '../utils/add-funds';
import { AddFundsFundingLine } from './add-funds-funding-line';
import * as styles from './add-funds.css';

const CUSTOM_OPTION = 'custom';

const OPTIONS = [
  ...ADD_FUNDS_PRESETS_DOLLARS.map((dollars) => ({ value: String(dollars), label: `$${dollars}` })),
  { value: CUSTOM_OPTION, label: 'Custom' },
];

type AddFundsAmountPanelProps = {
  selection: AmountSelection;
  balanceCents: number;
  funding?: AddFundsFunding;
  error: string | undefined;
  isDisabled: boolean;
  onChange: (selection: AmountSelection) => void;
  onCustomBlur: () => void;
};

export function AddFundsAmountPanel({
  selection,
  balanceCents,
  funding,
  error,
  isDisabled,
  onChange,
  onCustomBlur,
}: AddFundsAmountPanelProps) {
  const amountCents = (selection.dollars ?? 0) * 100;

  const onOptionChange = (value: string) => {
    if (value === CUSTOM_OPTION) {
      onChange({ kind: 'custom', dollars: selection.dollars });
      return;
    }
    onChange({ kind: 'preset', dollars: Number(value) });
  };

  return (
    <Box data-test-add-funds-amount className={styles.optionBox}>
      <Stack gap="200">
        <AmountFigure amountCents={amountCents} />
        <SegmentedControl
          label="Amount"
          ariaLabel="Amount to add"
          name="add-funds-amount"
          value={selection.kind === 'custom' ? CUSTOM_OPTION : String(selection.dollars)}
          isDisabled={isDisabled}
          onChange={onOptionChange}
          options={OPTIONS}
        />
        <CoveringTag funding={funding} selection={selection} />
        {selection.kind === 'custom' ? (
          <NumberField
            label="Custom amount"
            value={selection.dollars ?? undefined}
            withControls={false}
            min={0}
            elementBefore={
              <Text color="secondary" pl="150">
                $
              </Text>
            }
            description={`Any whole amount from ${CUSTOM_AMOUNT_RANGE}.`}
            error={error}
            isInvalid={error != null}
            isDisabled={isDisabled}
            onChange={(dollars) => onChange({ kind: 'custom', dollars: dollars ?? null })}
            onBlur={onCustomBlur}
          />
        ) : null}
        {selection.kind === 'preset' && error != null ? (
          <Text color="critical" size="sm">
            {error}
          </Text>
        ) : null}
        <Stack gap="050">
          {funding == null ? null : <AddFundsFundingLine funding={funding} amountCents={amountCents} />}
          <Text color="secondary" size="sm" data-test-add-funds-new-balance>
            New balance <Strong color="inherit">{formatCentsToUsd(balanceCents + amountCents)}</Strong>
          </Text>
        </Stack>
      </Stack>
    </Box>
  );
}

type CoveringTagProps = {
  funding: AddFundsFunding | undefined;
  selection: AmountSelection;
};

function CoveringTag({ funding, selection }: CoveringTagProps) {
  if (funding == null || selection.kind !== 'preset') return null;
  const tag = fundingTag(funding);
  if (tag == null || selection.dollars !== coveringAmountDollars(funding.needCents)) return null;
  return (
    <Text color="accent" weight="bold" size="sm" data-test-add-funds-covering-tag>
      {tag}
    </Text>
  );
}

type AmountFigureProps = {
  amountCents: number;
};

function AmountFigure({ amountCents }: AmountFigureProps) {
  return (
    <Stack gap="050">
      <Heading level="3" size="sm">
        Credits
      </Heading>
      <InlineStack gap="100" alignBlock="baseline">
        <Heading level="4" size="xl" data-test-add-funds-amount-figure>
          {formatCentsToUsd(amountCents, 0)}
        </Heading>
        <Text color="secondary">added to your balance</Text>
      </InlineStack>
      <Text color="secondary" size="sm">
        No monthly fee
      </Text>
    </Stack>
  );
}
